import { inject, Injectable } from '@angular/core';
import { TranslateLoader } from '@ngx-translate/core';
import { catchError, map, of } from 'rxjs';
import { PLAIN_HTTP_CLIENT } from '../tokens/http-tokens';
import { LocalStorageService } from './storage/local-storage.service';

/**
 * Language id from GET /v4.0/lookup/languages → ISO1 for `public/i18n/{code}.json`.
 * Must cover every language we ship locally; others fall back to `en` in getLanguageCode().
 * @see App.Cropmap.Language.File.URL in /v4.0/config (CropMap translation bundles).
 */
const LANG_GUID_TO_ISO1: Record<string, string> = {
    '24c9b00d-d028-4e04-a1aa-f04d2dcae2c3': 'en',
    '26c7ace9-13fc-43b8-9988-2384fe670d03': 'tr',
    'ed2c6ad0-96f0-46d9-a909-93b873db2394': 'es',
};

@Injectable({
    providedIn: 'root',
})
export class ConfigAwareTranslateLoader implements TranslateLoader {
    private readonly http = inject(PLAIN_HTTP_CLIENT);
    private readonly localStorage = inject(LocalStorageService);

    getTranslation(lang: string) {
        // For now: read lang JSONs from public i18n only
        const code = this.getLanguageCode(lang);
        const url = `i18n/${code}.json?v=${Date.now()}`;
        return this.http.get(url).pipe(
            map((res: any) => this.groupifyTranslations(res)),
            catchError(() => of({}))
        );

        // --- Original implementation (config + remote URLs, then fallback to local): ---
        // try {
        //     let currentLanguageCode = localStorage.getItem('languageISO1');
        //
        //     if (!currentLanguageCode) {
        //         if (lang === '24c9b00d-d028-4e04-a1aa-f04d2dcae2c3') {
        //             currentLanguageCode = 'en';
        //         } else {
        //             const availableLanguages = this.appConfigService.getAvailableLanguages();
        //             const availableKeys = this.appConfigService.getAvailableLanguageKeys();
        //
        //             if (availableLanguages.includes(lang)) {
        //                 currentLanguageCode = lang;
        //             } else if (availableKeys.includes(lang)) {
        //                 currentLanguageCode = this.appConfigService.keyToIso1(lang) || 'en';
        //             } else {
        //                 currentLanguageCode = availableLanguages.includes('en')
        //                     ? 'en'
        //                     : availableLanguages[0] || 'en';
        //             }
        //         }
        //     }
        //
        //     const localizationConfig = this.appConfigService.localizationConfig;
        //
        //     if (currentLanguageCode && localizationConfig && localizationConfig.length > 0) {
        //         const configUrl = this.appConfigService.localizationUrl(currentLanguageCode);
        //
        //         if (configUrl) {
        //             const url = `${configUrl}?v=${new Date().getTime()}`;
        //             return this.http.get(url).pipe(map((res: any) => this.groupifyTranslations(res)));
        //         }
        //
        //         const availableLanguages = this.appConfigService.getAvailableLanguages();
        //         if (availableLanguages.length > 0) {
        //             const fallbackLang = availableLanguages.includes('en') ? 'en' : availableLanguages[0];
        //             const fallbackUrl = this.appConfigService.localizationUrl(fallbackLang);
        //             if (fallbackUrl) {
        //                 const url = `${fallbackUrl}?v=${new Date().getTime()}`;
        //                 return this.http.get(url).pipe(map((res: any) => this.groupifyTranslations(res)));
        //             }
        //         }
        //     }
        //
        //     // Fallback: load from local assets
        //     const localUrl = `i18n/${currentLanguageCode || 'en'}.json?v=${new Date().getTime()}`;
        //     return this.http.get(localUrl).pipe(map((res: any) => this.groupifyTranslations(res)));
        // } catch (error) {
        //     console.error('Error loading translations from configuration:', error);
        // }
        //
        // return of({});
    }

    private getLanguageCode(lang: string): string {
        const fromStorage = this.localStorage.getItem<string>('languageISO1');
        if (fromStorage) return fromStorage;
        if (LANG_GUID_TO_ISO1[lang]) return LANG_GUID_TO_ISO1[lang];
        // Assume 2-letter code (e.g. en, tr)
        if (lang?.length === 2) return lang.toLowerCase();
        return 'en';
    }

    private groupifyTranslations(translations: any): any {
        const transformed: any = { ...translations };

        const primengKeys = Object.keys(translations).filter((key) => key.startsWith('primeng.'));

        if (primengKeys.length > 0) {
            transformed.primeng = {};

            primengKeys.forEach((key) => {
                const nestedKey = key.substring('primeng.'.length);
                let value = translations[key];

                if (typeof value === 'string' && (value.startsWith('[') || value.startsWith('{'))) {
                    try {
                        value = JSON.parse(value);
                    } catch (e) {
                        console.warn(`Failed to parse primeng.${nestedKey}:`, e);
                    }
                }

                transformed.primeng[nestedKey] = value;
                delete transformed[key];
            });
        }

        return transformed;
    }
}
